import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Input } from '../ui/Input';

type DialogKind = 'alert' | 'confirm' | 'prompt';

interface DialogRequest {
  id: number;
  kind: DialogKind;
  message: string;
  title?: string;
  defaultValue?: string;
  resolve: (value: boolean | string | null) => void;
}

interface DialogOptions {
  title?: string;
}

interface PromptOptions extends DialogOptions {
  defaultValue?: string;
}

type AlertFn = (message: string, options?: DialogOptions) => Promise<void>;
type ConfirmFn = (message: string, options?: DialogOptions) => Promise<boolean>;
type PromptFn = (message: string, options?: PromptOptions) => Promise<string | null>;

interface DialogContextValue {
  alert: AlertFn;
  confirm: ConfirmFn;
  prompt: PromptFn;
}

const DialogContext = createContext<DialogContextValue | null>(null);

let externalAlert: AlertFn = async (message) => {
  console.warn('App dialog provider is not mounted. Alert was not shown:', message);
};

let externalConfirm: ConfirmFn = async (message) => {
  console.warn('App dialog provider is not mounted. Confirmation was cancelled:', message);
  return false;
};

let externalPrompt: PromptFn = async (message, options) => {
  console.warn('App dialog provider is not mounted. Prompt was cancelled:', message, options?.defaultValue);
  return null;
};

const titles: Record<DialogKind, string> = {
  alert: 'Notice',
  confirm: 'Confirm Action',
  prompt: 'Input Required',
};

function formatMessage(message: string) {
  return message.split('\n').map((line, index) => (
    <React.Fragment key={`${index}-${line}`}>
      {line}
      {index < message.split('\n').length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

export function AppDialogProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const [promptValue, setPromptValue] = useState('');
  const idRef = useRef(0);

  const activeDialog = queue[0] ?? null;

  useEffect(() => {
    setPromptValue(activeDialog?.defaultValue ?? '');
  }, [activeDialog?.id, activeDialog?.defaultValue]);

  const enqueue = useCallback(
    <T extends boolean | string | null>(
      kind: DialogKind,
      message: string,
      options?: DialogOptions & { defaultValue?: string }
    ) =>
      new Promise<T>((resolve) => {
        const request: DialogRequest = {
          id: idRef.current + 1,
          kind,
          message,
          title: options?.title,
          defaultValue: options?.defaultValue,
          resolve: resolve as DialogRequest['resolve'],
        };
        idRef.current = request.id;
        setQueue((current) => [...current, request]);
      }),
    []
  );

  const closeActive = useCallback(
    (value: boolean | string | null) => {
      if (!activeDialog) return;
      activeDialog.resolve(value);
      setQueue((current) => current.slice(1));
    },
    [activeDialog]
  );

  const api = useMemo<DialogContextValue>(
    () => ({
      alert: async (message, options) => {
        await enqueue<null>('alert', message, options);
      },
      confirm: (message, options) => enqueue<boolean>('confirm', message, options),
      prompt: (message, options) => enqueue<string | null>('prompt', message, options),
    }),
    [enqueue]
  );

  useEffect(() => {
    externalAlert = api.alert;
    externalConfirm = api.confirm;
    externalPrompt = api.prompt;
  }, [api]);

  const isDestructive = activeDialog?.message.toLowerCase().includes('delete') ||
    activeDialog?.message.toLowerCase().includes('cannot be undone') ||
    activeDialog?.message.toLowerCase().includes('lost forever');

  return (
    <DialogContext.Provider value={api}>
      {children}
      <Dialog open={Boolean(activeDialog)} onOpenChange={(open) => !open && closeActive(activeDialog?.kind === 'confirm' ? false : null)}>
        <DialogContent className="max-w-md">
          {activeDialog ? (
            <>
              <DialogHeader>
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  {activeDialog.kind === 'alert' ? <Info className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                </div>
                <DialogTitle>{activeDialog.title ?? titles[activeDialog.kind]}</DialogTitle>
                <DialogDescription className="whitespace-normal text-gray-600">
                  {formatMessage(activeDialog.message)}
                </DialogDescription>
              </DialogHeader>

              {activeDialog.kind === 'prompt' ? (
                <Input
                  value={promptValue}
                  onChange={(event) => setPromptValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      closeActive(promptValue);
                    }
                  }}
                  autoFocus
                />
              ) : null}

              <DialogFooter>
                {activeDialog.kind === 'alert' ? (
                  <Button onClick={() => closeActive(null)}>OK</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => closeActive(activeDialog.kind === 'prompt' ? null : false)}>
                      Cancel
                    </Button>
                    <Button
                      variant={isDestructive ? 'destructive' : 'default'}
                      onClick={() => closeActive(activeDialog.kind === 'prompt' ? promptValue : true)}
                    >
                      {activeDialog.kind === 'prompt' ? 'Submit' : 'Confirm'}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </DialogContext.Provider>
  );
}

export function useAppDialogs() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useAppDialogs must be used within AppDialogProvider');
  }
  return context;
}

export const showAlert: AlertFn = (message, options) => externalAlert(message, options);
export const showConfirm: ConfirmFn = (message, options) => externalConfirm(message, options);
export const showPrompt: PromptFn = (message, options) => externalPrompt(message, options);
